import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { MessageSquare, RefreshCw, CheckCircle2, QrCode, LogOut, Trash2, AlertTriangle, Shield, Info, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WhatsApp() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [cleaningAll, setCleaningAll] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    fetchStatus();
    
    // Atualizar a cada 10 segundos (mais frequente para detectar desconexões rápido)
    intervalRef.current = setInterval(() => {
      fetchStatus();
    }, 10000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/whatsapp/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newStatus = response.data;
      
      // Detectar mudança de conectado para desconectado
      if (status?.connected && !newStatus.connected && newStatus.status !== 'qr_code') {
        toast.warning('WhatsApp desconectado! A sessão será limpa automaticamente.', {
          duration: 5000
        });
      }
      
      // Se receber QR code automaticamente após limpeza, notificar
      if (!status?.qr_code && newStatus.qr_code && newStatus.message?.includes('limpa')) {
        toast.success('Sessão limpa! Novo QR Code disponível.', {
          duration: 4000
        });
      }
      
      setStatus(newStatus);
    } catch (error) {
      console.error('Erro ao carregar status:', error);
      setStatus({
        connected: false,
        status: 'error',
        message: 'Erro ao conectar com o serviço'
      });
    } finally {
      setLoading(false);
    }
  };

  const startSession = async () => {
    setStarting(true);
    toast.loading('Conectando ao WhatsApp...', { id: 'starting', duration: 30000 });

    try {
      const token = localStorage.getItem('token');
      
      // Usar o endpoint robusto de conexão
      const response = await axios.post(`${API}/whatsapp/robust-connect`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000 // 30 segundos timeout
      });
      
      const data = response.data;
      
      if (data.success) {
        if (data.status === 'connected') {
          toast.success(data.message || 'WhatsApp conectado!', { id: 'starting' });
          setStatus({
            connected: true,
            status: 'connected',
            phone: data.phone,
            message: data.message
          });
        } else if (data.qr_code) {
          toast.success('QR Code gerado! Escaneie para conectar.', { id: 'starting' });
          setStatus({
            connected: false,
            status: 'qr_code',
            qr_code: data.qr_code,
            message: data.message
          });
        } else {
          toast.success(data.message || 'Sessão iniciada!', { id: 'starting' });
          setTimeout(() => fetchStatus(), 2000);
        }
      } else {
        // Mostrar erro com dicas
        toast.error(data.message || 'Erro ao conectar', { id: 'starting', duration: 5000 });
        
        // Atualizar status
        setStatus({
          connected: false,
          status: data.status || 'error',
          message: data.message,
          troubleshooting: data.troubleshooting
        });
      }
    } catch (error) {
      console.error('Erro ao conectar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao conectar. Tente novamente.', { id: 'starting' });
    } finally {
      setStarting(false);
    }
  };

  const restartSession = async () => {
    setStarting(true);
    toast.loading('Reconectando...', { id: 'restarting', duration: 30000 });

    try {
      const token = localStorage.getItem('token');
      
      // Usar o endpoint robusto de conexão
      const response = await axios.post(`${API}/whatsapp/robust-connect`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      
      const data = response.data;
      
      if (data.success && data.qr_code) {
        toast.success('Novo QR Code gerado!', { id: 'restarting' });
        setStatus({
          connected: false,
          status: 'qr_code',
          qr_code: data.qr_code,
          message: data.message
        });
      } else if (data.success && data.status === 'connected') {
        toast.success('WhatsApp já está conectado!', { id: 'restarting' });
        fetchStatus();
      } else {
        toast.error(data.message || 'Erro ao reconectar', { id: 'restarting' });
      }
    } catch (error) {
      toast.error('Erro ao reconectar. Tente "Limpar Tudo e Reconectar".', { id: 'restarting' });
    } finally {
      setStarting(false);
    }
  };

  const logoutSession = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar? Você precisará escanear um novo QR Code.')) {
      return;
    }

    setLoggingOut(true);
    toast.loading('Desconectando WhatsApp...', { id: 'logout' });

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/whatsapp/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('WhatsApp desconectado! Você pode conectar um novo número agora.', { id: 'logout' });
      
      // Atualizar status e iniciar nova sessão automaticamente
      setTimeout(() => {
        fetchStatus();
        startSession();
      }, 1000);
    } catch (error) {
      toast.error('Erro ao desconectar', { id: 'logout' });
      console.error('Error:', error);
    } finally {
      setLoggingOut(false);
    }
  };

  const cleanAllSessions = async () => {
    if (!window.confirm('⚠️ ATENÇÃO: Isso irá limpar TODAS as sessões antigas do WhatsApp e criar uma nova conexão do zero.\n\nDeseja continuar?')) {
      return;
    }

    setCleaningAll(true);
    toast.loading('Limpando todas as sessões...', { id: 'cleaning', duration: 30000 });

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/whatsapp/clean-all-sessions`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000 // 60 segundos timeout
      });
      
      if (response.data.success) {
        toast.success(response.data.message, { id: 'cleaning', duration: 5000 });
        
        // Se já recebeu QR code, atualizar status
        if (response.data.qr_code) {
          setStatus({
            connected: false,
            status: 'qr_code',
            qr_code: response.data.qr_code,
            message: 'Sessão limpa! Escaneie o novo QR Code.'
          });
        } else {
          // Aguardar e buscar status
          setTimeout(() => fetchStatus(), 2000);
        }
      } else {
        toast.error('Erro na limpeza', { id: 'cleaning' });
      }
    } catch (error) {
      console.error('Error cleaning sessions:', error);
      toast.error(error.response?.data?.detail || 'Erro ao limpar sessões', { id: 'cleaning' });
    } finally {
      setCleaningAll(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-zinc-950">
      <Sidebar />
      <main className="flex-1 md:ml-64 p-4 md:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          
          {/* Banner de Aviso sobre Segurança */}
          {showBanner && (
            <div className="mb-6 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-4 relative">
              <button 
                onClick={() => setShowBanner(false)}
                className="absolute top-3 right-3 text-amber-400 hover:text-amber-300"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-start gap-4">
                <div className="p-2 bg-amber-500/20 rounded-lg shrink-0">
                  <Shield className="w-6 h-6 text-amber-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-amber-400 font-bold text-lg mb-1">
                    ⚠️ Aviso Importante sobre Mensagem do WhatsApp
                  </h3>
                  <p className="text-amber-200/80 text-sm mb-3">
                    O WhatsApp pode exibir uma mensagem de <strong>"Suspeita de golpe"</strong> ao escanear o QR Code. 
                    <strong> Isso é apenas uma tentativa do WhatsApp de desencorajar o uso de APIs não oficiais.</strong>
                  </p>
                  <button 
                    onClick={() => setShowSecurityModal(true)}
                    className="inline-flex items-center gap-2 text-sm font-medium text-amber-400 hover:text-amber-300 underline"
                  >
                    <Info className="w-4 h-4" />
                    Entenda por que nosso sistema é seguro
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal de Segurança */}
          <Dialog open={showSecurityModal} onOpenChange={setShowSecurityModal}>
            <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3 text-xl">
                  <div className="p-2 bg-emerald-500/20 rounded-lg">
                    <Shield className="w-6 h-6 text-emerald-500" />
                  </div>
                  Nosso Sistema é Seguro!
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <h4 className="text-amber-400 font-semibold mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Por que o WhatsApp mostra esse aviso?
                  </h4>
                  <p className="text-slate-300 text-sm">
                    O WhatsApp recentemente começou a exibir mensagens de "Suspeita de golpe" quando detecta 
                    conexões via APIs não oficiais. <strong>Isso NÃO significa que há qualquer risco real.</strong> 
                    É apenas uma estratégia do WhatsApp para tentar limitar o uso dessas ferramentas.
                  </p>
                </div>

                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                  <h4 className="text-emerald-400 font-semibold mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    Por que você pode confiar no nosso sistema?
                  </h4>
                  <ul className="text-slate-300 text-sm space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">✓</span>
                      <span>Seus dados são criptografados e nunca compartilhados</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">✓</span>
                      <span>A conexão é feita diretamente entre seu celular e nossa plataforma</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">✓</span>
                      <span>Milhares de usuários utilizam nosso sistema diariamente sem problemas</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">✓</span>
                      <span>Você pode desconectar a qualquer momento pelo próprio WhatsApp</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-4">
                  <h4 className="text-sky-400 font-semibold mb-2 flex items-center gap-2">
                    <Info className="w-5 h-5" />
                    O que fazer?
                  </h4>
                  <p className="text-slate-300 text-sm">
                    Ao ver a mensagem de "Suspeita de golpe", basta <strong>ignorar o aviso e continuar com a vinculação</strong>. 
                    Seu WhatsApp funcionará normalmente e você poderá usar todas as funcionalidades do nosso sistema com segurança.
                  </p>
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button 
                  onClick={() => setShowSecurityModal(false)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  Entendi, pode continuar!
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-500 rounded-lg">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">WhatsApp</h1>
                <p className="text-slate-400">Conecte seu WhatsApp para enviar mensagens</p>
              </div>
            </div>
            <Button
              onClick={fetchStatus}
              variant="outline"
              size="sm"
              disabled={loading}
              className="border-zinc-700 hover:bg-zinc-800 text-slate-300"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>

          {loading ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mb-4"></div>
              <p className="text-slate-400">Carregando status...</p>
            </div>
          ) : (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8">
              {status?.connected ? (
                // CONECTADO
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-500/20 rounded-full mb-6">
                    <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    WhatsApp Conectado!
                  </h2>
                  <p className="text-slate-300 mb-2">
                    {status.phone || 'Conectado com sucesso'}
                  </p>
                  {status.waha_session && (
                    <p className="text-sm text-violet-400 mb-2">
                      Instância: <span className="font-mono font-semibold">{status.waha_session}</span>
                    </p>
                  )}
                  <p className="text-sm text-slate-400 mb-8">
                    Seu WhatsApp está pronto para enviar mensagens
                  </p>
                  
                  <div className="flex flex-wrap gap-3 justify-center">
                    <Button
                      onClick={restartSession}
                      variant="outline"
                      disabled={starting || loggingOut || cleaningAll}
                      className="border-zinc-700 hover:bg-zinc-800 text-slate-300"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${starting ? 'animate-spin' : ''}`} />
                      Reconectar
                    </Button>
                    <Button
                      onClick={logoutSession}
                      className="bg-rose-600 hover:bg-rose-700 text-white"
                      disabled={starting || loggingOut || cleaningAll}
                    >
                      <LogOut className={`w-4 h-4 mr-2 ${loggingOut ? 'animate-spin' : ''}`} />
                      {loggingOut ? 'Desconectando...' : 'Desconectar'}
                    </Button>
                    <Button
                      onClick={cleanAllSessions}
                      variant="outline"
                      disabled={starting || loggingOut || cleaningAll}
                      className="border-amber-600 text-amber-400 hover:bg-amber-900/30"
                      data-testid="btn-clean-sessions-connected"
                    >
                      <Trash2 className={`w-4 h-4 mr-2 ${cleaningAll ? 'animate-spin' : ''}`} />
                      {cleaningAll ? 'Limpando...' : 'Limpar Sessões'}
                    </Button>
                  </div>
                </div>
              ) : status?.status === 'qr_code' && status?.qr_code ? (
                // QR CODE
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-violet-500/20 rounded-full mb-6">
                    <QrCode className="w-8 h-8 text-violet-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    Escaneie o QR Code
                  </h2>
                  <p className="text-slate-300 mb-2">
                    Abra o WhatsApp no seu celular e escaneie o código abaixo
                  </p>
                  {status.waha_session && (
                    <p className="text-sm text-violet-400 mb-4">
                      Instância: <span className="font-mono font-semibold">{status.waha_session}</span>
                    </p>
                  )}
                  
                  {status.message?.includes('limpa') && (
                    <div className="mb-4 p-3 bg-emerald-900/20 border border-emerald-700 rounded-lg">
                      <p className="text-sm text-emerald-300">
                        ✅ {status.message}
                      </p>
                    </div>
                  )}
                  
                  <div className="inline-block p-6 bg-white border-4 border-zinc-700 rounded-lg mb-6">
                    <img
                      src={status.qr_code.startsWith('data:') ? status.qr_code : `data:image/png;base64,${status.qr_code}`}
                      alt="QR Code"
                      className="w-64 h-64"
                    />
                  </div>
                  
                  <p className="text-sm text-slate-400 mb-6">
                    ✨ O QR Code é atualizado automaticamente a cada 10 segundos
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                    <Button
                      onClick={restartSession}
                      variant="outline"
                      size="sm"
                      disabled={starting || cleaningAll}
                      className="border-zinc-700 hover:bg-zinc-800 text-slate-300"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${starting ? 'animate-spin' : ''}`} />
                      Gerar Novo QR Code
                    </Button>
                    
                    <Button
                      onClick={cleanAllSessions}
                      variant="outline"
                      size="sm"
                      disabled={starting || cleaningAll}
                      className="border-amber-600 text-amber-400 hover:bg-amber-900/30"
                      data-testid="btn-clean-sessions-qr"
                    >
                      <Trash2 className={`w-4 h-4 mr-2 ${cleaningAll ? 'animate-spin' : ''}`} />
                      {cleaningAll ? 'Limpando...' : 'Limpar Tudo e Reconectar'}
                    </Button>
                  </div>
                </div>
              ) : (
                // NÃO INICIADO / ERRO / ESTADO INDEFINIDO
                <div className="text-center">
                  <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    status?.status === 'error' ? 'bg-rose-900/30' : 'bg-zinc-800'
                  }`}>
                    {status?.status === 'error' ? (
                      <AlertTriangle className="w-10 h-10 text-rose-400" />
                    ) : (
                      <MessageSquare className="w-10 h-10 text-slate-400" />
                    )}
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    {status?.status === 'error' ? 'Erro na Conexão' : 'WhatsApp Desconectado'}
                  </h2>
                  <p className="text-slate-300 mb-4">
                    {status?.message || 'Inicie uma sessão para conectar seu WhatsApp'}
                  </p>
                  
                  {/* Dicas de Troubleshooting */}
                  {status?.troubleshooting && status.troubleshooting.length > 0 && (
                    <div className="mb-6 p-4 bg-amber-900/20 border border-amber-700 rounded-lg text-left max-w-md mx-auto">
                      <p className="text-sm font-medium text-amber-300 mb-2">💡 Dicas para resolver:</p>
                      <ul className="text-sm text-amber-200 space-y-1">
                        {status.troubleshooting.map((tip, index) => (
                          <li key={index}>• {tip}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                    <Button
                      onClick={startSession}
                      size="lg"
                      disabled={starting || cleaningAll}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="btn-connect-whatsapp"
                    >
                      <MessageSquare className="w-5 h-5 mr-2" />
                      {starting ? 'Conectando...' : 'Conectar WhatsApp'}
                    </Button>
                    
                    <Button
                      onClick={cleanAllSessions}
                      size="lg"
                      variant="outline"
                      disabled={starting || cleaningAll}
                      className="border-amber-600 text-amber-400 hover:bg-amber-900/30"
                      data-testid="btn-clean-sessions"
                    >
                      <Trash2 className={`w-5 h-5 mr-2 ${cleaningAll ? 'animate-spin' : ''}`} />
                      {cleaningAll ? 'Limpando...' : 'Limpar Sessões Antigas'}
                    </Button>
                  </div>
                  
                  {/* Aviso sobre limpar sessões */}
                  <div className="mt-6 p-4 bg-amber-900/20 border border-amber-700 rounded-lg max-w-md mx-auto">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                      <div className="text-left">
                        <p className="text-sm text-amber-300 font-medium mb-1">Problemas para conectar?</p>
                        <p className="text-xs text-amber-400/80">
                          Se o QR Code não aparecer ou der erro, use o botão &quot;Limpar Sessões Antigas&quot; para 
                          remover sessões travadas e criar uma conexão nova.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Info Box */}
          <div className="mt-6 bg-violet-900/20 border border-violet-800 rounded-lg p-4">
            <h3 className="font-semibold text-violet-300 mb-2">ℹ️ Informações</h3>
            <ul className="text-sm text-slate-400 space-y-1">
              <li>• Cada usuário possui sua própria sessão WhatsApp isolada</li>
              <li>• O QR Code expira após alguns minutos e é renovado automaticamente</li>
              <li>• A página verifica o status a cada 10 segundos</li>
              <li>• Se desconectar, a sessão será limpa e um novo QR Code será gerado automaticamente</li>
              <li>• <span className="text-amber-400 font-medium">Problemas para conectar?</span> Use &quot;Limpar Sessões Antigas&quot; para remover sessões travadas</li>
              <li>• Mantenha seu WhatsApp conectado para enviar mensagens</li>
            </ul>
          </div>
        </div>
      </main>
    </div>
  );
}
